export const CASE_STUDIES = [
  {
    id: "01",
    slug: "vistara",
    name: "Vistara",
    tagline: "Modular Web3 Infrastructure Landing Architecture",
    type: "Frontend Architecture & Systems",
    status: "Implemented & Active",
    verificationStatus: "Verified Production Repository",
    role: "Lead Frontend Engineer & Design Systems Architect",
    timeline: "2024",
    stack: ["Next.js", "Tailwind CSS", "Framer Motion", "TypeScript"],
    repoUrl: "https://github.com/Maverick-list/vistara",
    liveUrl: "https://vistara.network",
    overview:
      "Vistara is a high-performance modular landing and documentation platform engineered for Web3 infrastructure. It solves the friction of explaining decentralized network tooling through token-driven components, deterministic state handling, and fluid motion design.",
    problem:
      "Web3 infrastructure projects frequently suffer from visual fragmentation, bloated client-side scripts, and inaccessible contrast. Technical evaluators and developer audiences require instant clarity on network specifications, protocol mechanics, and developer tooling without performance lag.",
    goals: [
      "Achieve zero layout shifts (CLS < 0.05) and sub-second Largest Contentful Paint (LCP).",
      "Establish an atomic design token system matching strict brand tokens across responsive breakpoints.",
      "Ensure WCAG 2.2 AA accessibility across dark theme palettes with high keyboard focus fidelity.",
    ],
    architecture: `flowchart TD
    A[Next.js App Router] --> B[Design Token System]
    A --> C[Interactive Modular Grid]
    B --> D[Tailwind Custom Theme Engine]
    C --> E[Framer Motion Layer]
    C --> F[Network Stat Callouts]
    E --> G[WCAG AA Contrast & Motion Guards]`,
    decisions: [
      {
        topic: "CSS Modules vs Tailwind Design Tokens",
        decision: "Adopted Tailwind CSS configured with CSS variables.",
        tradeoff: "Required disciplined token governance in tailwind.config.js, but eliminated runtime CSS overhead and enabled rapid component prototyping.",
      },
      {
        topic: "Micro-Interactions vs Bundle Size",
        decision: "Implemented lightweight Framer Motion layout animations with prefers-reduced-motion guards.",
        tradeoff: "Added ~30kB to main client bundle, offset by lazy loading below-the-fold modules.",
      },
    ],
    features: [
      { name: "Token-Driven Interactive Grid", status: "Implemented", desc: "Dynamic component layouts highlighting modular execution layers." },
      { name: "Responsive Fluid Navigation", status: "Implemented", desc: "Scroll-aware glassmorphic navigation header with keyboard focus states." },
      { name: "Network Metric Counters", status: "Implemented", desc: "Performant lightweight number counting micro-animations." },
      { name: "Dynamic Documentation Hub", status: "Planned", desc: "Markdown/MDX-backed technical protocol specification viewer." },
    ],
    securityAccessibility:
      "Built with strict Content Security Policy headers, sanitized SVG assets, zero dangerous innerHTML injections, and full screen-reader label compliance on interactive controls.",
    limitations:
      "Live metric counters currently rely on static telemetry snapshots rather than real-time WebSocket node updates.",
  },
  {
    id: "02",
    slug: "mavecode-course",
    name: "MavecodeCourse",
    tagline: "Full-Stack Learning & Curriculum Progression Platform",
    type: "Full-Stack Platform & LMS",
    status: "Implemented & Active",
    verificationStatus: "Verified Production Repository",
    role: "Full-Stack Engineer & Database Designer",
    timeline: "2024 - 2025",
    stack: ["Next.js", "Node.js", "MongoDB", "Tailwind CSS"],
    repoUrl: "https://github.com/Maverick-list/mavecode-course",
    liveUrl: "https://mavecode.my.id",
    overview:
      "MavecodeCourse is a bespoke learning platform built to provide structured technical software engineering courses. It incorporates end-to-end user authentication, modular lesson progression, persistent student tracking, and an authoring interface.",
    problem:
      "Traditional open-source course sites lack coherent tracking and fast, distraction-free study interfaces. Students often lose context between video lessons, code tasks, and theoretical notes when navigating clunky LMS systems.",
    goals: [
      "Provide a frictionless authentication and enrollment journey.",
      "Deliver instant progress state synchronization across client sessions.",
      "Ensure student dashboards load under 1.2s on mobile connections.",
    ],
    architecture: `flowchart TD
    User[Student Browser] --> CDN[Vercel Edge Network]
    CDN --> NextApp[Next.js SSR & API Routes]
    NextApp --> Auth[Session & Token Validator]
    NextApp --> DB[(MongoDB Atlas Cluster)]
    DB --> Analytics[Student Progression Store]`,
    decisions: [
      {
        topic: "Database Architecture",
        decision: "Selected MongoDB with structured Mongoose schemas for flexible lesson hierarchies and progress arrays.",
        tradeoff: "Required custom aggregation pipelines for progress rollups, but made curriculum schema iterations frictionless.",
      },
      {
        topic: "Rendering Strategy",
        decision: "Hybrid SSG for static course outlines + SSR for authenticated student progress state.",
        tradeoff: "Cache invalidation required webhooks on course updates, but reduced database load by 60%.",
      },
    ],
    features: [
      { name: "Student Progress Persistence", status: "Implemented", desc: "Tracks completed exercises, quiz checkpoints, and time spent." },
      { name: "Modular Curriculum Player", status: "Implemented", desc: "Sidebar navigation synced with active video and markdown lectures." },
      { name: "Instructor Authoring Studio", status: "Implemented", desc: "Markdown editor for publishing structured modules and code snippets." },
      { name: "Automated Certificate Generation", status: "Planned", desc: "Vector PDF certificate issuance upon 100% course completion." },
    ],
    securityAccessibility:
      "HttpOnly cookies for JWT authentication, bcrypt password hashing with salt rounds, rate-limited login endpoints, and semantic tab navigation throughout the curriculum player.",
    limitations:
      "Automated code sandbox execution is currently linked out to third-party sandboxes rather than running in isolated server containers.",
  },
  {
    id: "03",
    slug: "mavementor-ai",
    name: "MaveMentor AI",
    tagline: "Context-Aware RAG Conversational Guidance Pipeline",
    type: "Applied AI & LLM Systems",
    status: "Implemented Architecture & Prototype",
    verificationStatus: "Verified Source Architecture",
    role: "AI Systems Engineer & Backend Developer",
    timeline: "2025",
    stack: ["Python", "FastAPI", "Gemini API", "Vector Embeddings"],
    repoUrl: "https://github.com/Maverick-list/mavementor-ai",
    liveUrl: null,
    overview:
      "MaveMentor AI is a retrieval-augmented generation (RAG) assistant engineered to mentor engineering students through curriculum-specific software hurdles without hallucinations or generic evasive answers.",
    problem:
      "General-purpose chatbots frequently hallucinate APIs, recommend outdated syntax, and lack awareness of the specific pedagogical roadmap a student is following in their local curriculum.",
    goals: [
      "Ground all model responses in verified syllabus documentation and verified code snippets.",
      "Achieve streaming token responses within 800ms of prompt submission.",
      "Implement prompt evaluation heuristics to prevent prompt injection and out-of-scope queries.",
    ],
    architecture: `flowchart LR
    Student[User Query] --> Gate[FastAPI Query Guard]
    Gate --> Embed[Embedding Model]
    Embed --> Search[(Vector Similarity Store)]
    Search --> Context[Retrieved Curriculum Chunks]
    Context --> LLM[Gemini Pro LLM Engine]
    LLM --> Stream[Streaming Response to Frontend]`,
    decisions: [
      {
        topic: "Inference Engine",
        decision: "Utilized Google Gemini 2.5 Flash API with custom system instructions.",
        tradeoff: "Requires external cloud API connectivity, but provides superior reasoning on complex code explanations at low latency.",
      },
      {
        topic: "Chunking Strategy",
        decision: "Hierarchical chunking preserving function declarations and markdown headings together.",
        tradeoff: "Slightly higher storage footprint in the vector store, but eliminated orphaned code snippets in retrieved context.",
      },
    ],
    features: [
      { name: "Curriculum-Aware Vector Retrieval", status: "Implemented", desc: "Retrieves only syllabus-approved documentation." },
      { name: "FastAPI Async Middleware", status: "Implemented", desc: "Asynchronous stream endpoints with CORS protection." },
      { name: "Hallucination Defense Checks", status: "In-Progress", desc: "Post-generation verification scoring groundedness against retrieved text." },
      { name: "Multi-modal Voice Input", status: "Planned", desc: "Direct audio-to-text pipeline for voice-first pair programming." },
    ],
    securityAccessibility:
      "Zero persistent storage of user conversational prompts without consent, strict prompt boundary isolation, and accessible keyboard-operable chat history drawer.",
    limitations:
      "Public production deployment is gated pending API quota management and multi-tenant authentication integration.",
  },
  {
    id: "04",
    slug: "ai-content-curation",
    name: "AI Content Curation",
    tagline: "Academic Informatics & Systematic Literature Review Tool",
    type: "Informatics & Empirical Research",
    status: "Implemented Research Pipeline",
    verificationStatus: "Verified Research Methodology",
    role: "Lead Researcher & Pipeline Architect",
    timeline: "2025 - 2026",
    stack: ["Python", "React", "SLR Methodology", "Informatics"],
    repoUrl: "https://github.com/Maverick-list/ai-content-curation",
    liveUrl: null,
    overview:
      "An automated academic informatics pipeline designed for Systematic Literature Reviews (SLR). It standardizes search queries across IEEE Xplore, ScienceDirect, and Google Scholar, eliminating duplicate publications and extracting evidence matrices.",
    problem:
      "Conducting SLRs manually is labor-intensive and prone to selection bias. Researchers waste dozens of hours deduplicating references, normalizing bibtex entries, and screening hundreds of abstracts that fail inclusion criteria.",
    goals: [
      "Automate duplicate identification across multi-database search exports with >99% precision.",
      "Provide an interactive screening interface with keyboard hotkeys for rapid accept/reject decisions.",
      "Export PRISMA-compliant flow statistics directly into LaTeX tables.",
    ],
    architecture: `flowchart TD
    Raw[Multi-Source BibTeX/CSV] --> Parser[BibTeX & DOI Normalizer]
    Parser --> Dedup[Fuzzy Matcher & Exact DOI Deduplication]
    Dedup --> Screen[Screening Dashboard UI]
    Screen --> Reviewer[Researcher Inclusion Decisions]
    Reviewer --> PRISMA[PRISMA Flow Metrics & Synthesis Export]`,
    decisions: [
      {
        topic: "Deduplication Algorithm",
        decision: "Hybrid exact DOI match + Levenshtein distance on title and authors.",
        tradeoff: "Slightly slower execution time during import (~3s per 1,000 papers), but prevented false merges of similarly named workshop papers.",
      },
      {
        topic: "Client UI Stack",
        decision: "Lightweight React UI paired with local JSON exports.",
        tradeoff: "Local file system operations required standard browser file pickers, but eliminated any need to upload proprietary research drafts to cloud servers.",
      },
    ],
    features: [
      { name: "Multi-Source DOI Dedup Engine", status: "Implemented", desc: "Normalizes author capitalization and removes cross-database duplicates." },
      { name: "PRISMA Diagram Metrics", status: "Implemented", desc: "Calculates records identified, screened, excluded, and included." },
      { name: "LLM Abstract Summarization", status: "In-Progress", desc: "Generates objective 2-sentence takeaways against user inclusion criteria." },
      { name: "Zotero / Mendeley Sync", status: "Planned", desc: "Bidirectional synchronization via local desktop APIs." },
    ],
    securityAccessibility:
      "Completely local client-side processing ensuring academic drafts and unpublished literature queries never leak to third-party endpoints.",
    limitations:
      "Abstract screening LLM inference currently requires local Ollama or API key configuration.",
  },
];
